import get from 'lodash/get';
import { getNumericMetric, isRateMetric } from './rateMetrics';
import { dataFilterMetricsList } from '@utils';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import { metricsStore } from '@globalStore/metricsStore';
import { metricMetaOvbOverrides } from '@utils/overview/metrics';
import { BPS } from './constants';
import { getInvertContextMap } from '@utils/common';
import { type AppFlowMetadata } from '@src/hooks/flow/useFlowConfigs';

interface MetricUtilsInputTypes {
  metricKey: string;
  context?: string;
  defaultValue?: any;
  hasSubscript?: boolean;
  flowOverride?: boolean;
  frustrationSignalOverride?: boolean;
}

export const getMetricDisplayName = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = metricKey,
  flowOverride = false,
  frustrationSignalOverride = false,
}: MetricUtilsInputTypes) => {
  const { metricsMeta, flowMetricsMeta, frustrationSignalMetricsMeta } = metricsStore.getState();
  const currentContext = getInvertContextMap(context);
  if (!metricsMeta) return defaultValue;

  if (frustrationSignalOverride && frustrationSignalMetricsMeta) {
    return (
      get(frustrationSignalMetricsMeta, `${currentContext}.${metricKey}.displayName`) ||
      get(metricsMeta, `${currentContext}.${metricKey}.displayName`) ||
      defaultValue
    );
  }

  if (flowOverride && flowMetricsMeta) {
    const flowMetadata = get(flowMetricsMeta, `${currentContext}.${metricKey}`);
    const baseDisplayName =
      get(flowMetadata, 'displayName') ||
      get(metricsMeta, `${currentContext}.${metricKey}.displayName`) ||
      defaultValue;
    // Append "avg" prefix when computation method is average to clarify aggregation type
    const computationMethod = get(flowMetadata, 'computationMethod');
    return computationMethod === 'average' ? `avg ${baseDisplayName}` : baseDisplayName;
  }

  return get(metricsMeta, `${currentContext}.${metricKey}.displayName`) || defaultValue;
};

export const getMetricSubscript = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = '',
}: MetricUtilsInputTypes) => {
  const currentContext = getInvertContextMap(context);
  const { metricsMeta } = metricsStore.getState();
  return get(metricsMeta, `${currentContext}.${metricKey}.subscript`) || defaultValue;
};

export const getMetricOvbDisplayName = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = metricKey,
}: MetricUtilsInputTypes) => {
  const currentContext = getInvertContextMap(context);
  const { metricsMeta } = metricsStore.getState();
  const metricOverrideMeta = metricMetaOvbOverrides[metricKey];
  if (metricOverrideMeta) {
    return metricOverrideMeta.displayName || defaultValue;
  }
  return get(metricsMeta, `${currentContext}.${metricKey}.displayName`) || defaultValue;
};

export const getMetricOvbShortName = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = metricKey,
}: MetricUtilsInputTypes) => {
  const currentContext = getInvertContextMap(context);
  const { metricsMeta } = metricsStore.getState();
  const metricOverrideMeta = metricMetaOvbOverrides[metricKey];
  if (metricOverrideMeta) {
    return metricOverrideMeta.short || defaultValue;
  }
  const metricName = get(metricsMeta, `${currentContext}.${metricKey}.short`) || defaultValue;

  return metricName;
};

export const getSecondaryMetric = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = null,
  flowOverride = false,
  frustrationSignalOverride = false,
}: MetricUtilsInputTypes) => {
  const currentContext = getInvertContextMap(context);
  const { metricsMeta, flowMetricsMeta, frustrationSignalMetricsMeta } = metricsStore.getState();

  if (frustrationSignalOverride && frustrationSignalMetricsMeta) {
    return (
      get(frustrationSignalMetricsMeta, `${currentContext}.${metricKey}.defaultSecondaryMetric`) ||
      get(metricsMeta, `${currentContext}.${metricKey}.defaultSecondaryMetric`) ||
      defaultValue
    );
  }

  if (flowOverride && flowMetricsMeta) {
    return (
      get(flowMetricsMeta, `${currentContext}.${metricKey}.defaultSecondaryMetric`) ||
      get(metricsMeta, `${currentContext}.${metricKey}.defaultSecondaryMetric`) ||
      defaultValue
    );
  }

  return get(metricsMeta, `${currentContext}.${metricKey}.defaultSecondaryMetric`) || defaultValue;
};

export const getMetricHelpText = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = '',
  flowOverride = false,
  frustrationSignalOverride = false,
}: MetricUtilsInputTypes) => {
  const currentContext = getInvertContextMap(context);
  const { metricsMeta, flowMetricsMeta, frustrationSignalMetricsMeta } = metricsStore.getState();

  if (frustrationSignalOverride && frustrationSignalMetricsMeta) {
    return (
      get(frustrationSignalMetricsMeta, `${currentContext}.${metricKey}.helpText`) ||
      get(metricsMeta, `${currentContext}.${metricKey}.helpText`) ||
      defaultValue
    );
  }

  if (flowOverride && flowMetricsMeta) {
    return (
      get(flowMetricsMeta, `${currentContext}.${metricKey}.helpText`) ||
      get(metricsMeta, `${currentContext}.${metricKey}.helpText`) ||
      defaultValue
    );
  }

  return get(metricsMeta, `${currentContext}.${metricKey}.helpText`) || defaultValue;
};

export const getMetricPercentageMetric = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = metricKey,
  flowOverride = false,
  frustrationSignalOverride = false,
}: MetricUtilsInputTypes) => {
  const currentContext = getInvertContextMap(context);
  const { metricsMeta, flowMetricsMeta, frustrationSignalMetricsMeta } = metricsStore.getState();

  if (frustrationSignalOverride && frustrationSignalMetricsMeta) {
    return (
      get(frustrationSignalMetricsMeta, `${currentContext}.${metricKey}.percentageMetric`) ||
      get(metricsMeta, `${currentContext}.${metricKey}.percentageMetric`) ||
      defaultValue
    );
  }

  if (flowOverride && flowMetricsMeta) {
    return (
      get(flowMetricsMeta, `${currentContext}.${metricKey}.percentageMetric`) ||
      get(metricsMeta, `${currentContext}.${metricKey}.percentageMetric`) ||
      defaultValue
    );
  }

  return get(metricsMeta, `${currentContext}.${metricKey}.percentageMetric`) || defaultValue;
};

export const getMetricMetaByKey = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = {},
  flowOverride = false,
  frustrationSignalOverride = false,
}: MetricUtilsInputTypes) => {
  const currentContext = getInvertContextMap(context);
  const { metricsMeta, flowMetricsMeta, frustrationSignalMetricsMeta } = metricsStore.getState();

  if (frustrationSignalOverride && frustrationSignalMetricsMeta) {
    return (
      get(frustrationSignalMetricsMeta, `${currentContext}.${metricKey}`) ||
      get(metricsMeta, `${currentContext}.${metricKey}`) ||
      defaultValue
    );
  }

  if (flowOverride && flowMetricsMeta) {
    return (
      get(flowMetricsMeta, `${currentContext}.${metricKey}`) ||
      get(metricsMeta, `${currentContext}.${metricKey}`) ||
      defaultValue
    );
  }

  return get(metricsMeta, `${currentContext}.${metricKey}`) || defaultValue;
};

export const getMetricFlowMeta = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
}: MetricUtilsInputTypes) => {
  const currentContext = getInvertContextMap(context);
  const { metricsMeta, flowConfigMeta } = metricsStore.getState();
  const flowID = get(metricsMeta, `${currentContext}.${metricKey}.flowID`);
  // There is no percentage metric in flow metrics definition, so have to use the relevant count metric to find the flow metric metadata
  const invertedPercentageMetric = getInvertedPercentageMetricByKey({
    metricKey,
    defaultValue: metricKey,
  });

  return flowConfigMeta?.[flowID]?.metaDefinition.metrics.find(
    (item) => item.identifier === metricKey || item.identifier === invertedPercentageMetric,
  ) as AppFlowMetadata['metaDefinition']['metrics'][number] | undefined;
};

export const shouldCalcPercentage = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = false,
  flowOverride = false,
  frustrationSignalOverride = false,
}: MetricUtilsInputTypes) => {
  const currentContext = getInvertContextMap(context);
  const { metricsMeta, flowMetricsMeta, frustrationSignalMetricsMeta } = metricsStore.getState();

  if (frustrationSignalOverride && frustrationSignalMetricsMeta) {
    return (
      get(frustrationSignalMetricsMeta, `${currentContext}.${metricKey}.calcPercentage`) ||
      get(metricsMeta, `${currentContext}.${metricKey}.calcPercentage`) ||
      defaultValue
    );
  }

  if (flowOverride && flowMetricsMeta) {
    return (
      get(flowMetricsMeta, `${currentContext}.${metricKey}.calcPercentage`) ||
      get(metricsMeta, `${currentContext}.${metricKey}.calcPercentage`) ||
      defaultValue
    );
  }

  return get(metricsMeta, `${currentContext}.${metricKey}.calcPercentage`) || defaultValue;
};

// returns metric unit name
export const getMetricUnitByKey = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = '',
  flowOverride = false,
  frustrationSignalOverride = false,
}: MetricUtilsInputTypes) => {
  const currentContext = getInvertContextMap(context);
  const { metricsMeta, flowMetricsMeta, frustrationSignalMetricsMeta } = metricsStore.getState();

  if (frustrationSignalOverride && frustrationSignalMetricsMeta) {
    return (
      get(frustrationSignalMetricsMeta, `${currentContext}.${metricKey}.unit`) ||
      get(metricsMeta, `${currentContext}.${metricKey}.unit`) ||
      defaultValue
    );
  }

  if (flowOverride && flowMetricsMeta) {
    return (
      get(flowMetricsMeta, `${currentContext}.${metricKey}.unit`) ||
      get(metricsMeta, `${currentContext}.${metricKey}.unit`) ||
      defaultValue
    );
  }

  return get(metricsMeta, `${currentContext}.${metricKey}.unit`) || defaultValue;
};

export const getMetricShortName = ({
  metricKey,
  hasSubscript = false,
  context = CURRENT_PRODUCT_MODE,
  flowOverride = false,
  frustrationSignalOverride = false,
  defaultValue = {
    short: metricKey,
    subScript: '',
  },
}: MetricUtilsInputTypes) => {
  const currentContext = getInvertContextMap(context);
  const { metricsMeta, flowMetricsMeta, frustrationSignalMetricsMeta } = metricsStore.getState();

  let metricName;
  if (frustrationSignalOverride && frustrationSignalMetricsMeta) {
    metricName =
      get(frustrationSignalMetricsMeta, `${currentContext}.${metricKey}.short`) ||
      get(metricsMeta, `${currentContext}.${metricKey}.short`) ||
      defaultValue.short;
  } else if (flowOverride && flowMetricsMeta) {
    metricName =
      get(flowMetricsMeta, `${currentContext}.${metricKey}.short`) ||
      get(metricsMeta, `${currentContext}.${metricKey}.short`) ||
      defaultValue.short;
  } else {
    metricName = get(metricsMeta, `${currentContext}.${metricKey}.short`) || defaultValue.short;
  }

  if (!hasSubscript) {
    return metricName;
  }
  const subscript =
    get(metricsMeta, `${currentContext}.${metricKey}.subscript`) || defaultValue.subScript;
  return subscript ? `${metricName} ${subscript}` : metricName;
};

export const getPercentageMetricByKey = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = null,
}: {
  metricKey: string;
  context?: string;
  defaultValue?: null | string;
}) => {
  const currentContext = getInvertContextMap(context);
  const { percentageMetricsMap } = metricsStore.getState();
  if (percentageMetricsMap[currentContext] && percentageMetricsMap[currentContext][metricKey]) {
    return percentageMetricsMap[currentContext][metricKey];
  } else {
    return defaultValue;
  }
};

export const getInvertedPercentageMetricByKey = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = null,
}: {
  metricKey: string;
  context?: string;
  defaultValue?: null | string;
}) => {
  const currentContext = getInvertContextMap(context);
  const { invertPercentageMetricsMap } = metricsStore.getState();
  if (
    invertPercentageMetricsMap[currentContext] &&
    invertPercentageMetricsMap[currentContext][metricKey]
  ) {
    return invertPercentageMetricsMap[currentContext][metricKey];
  } else {
    return defaultValue;
  }
};

export const getInvertedSecondaryOnlyMetricByKey = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = [],
}: {
  metricKey: string;
  context?: string;
  defaultValue?: [] | [string];
}) => {
  const currentContext = getInvertContextMap(context);
  const { invertSecondaryOnlyMetricsKeyMap } = metricsStore.getState();
  if (
    invertSecondaryOnlyMetricsKeyMap[currentContext] &&
    invertSecondaryOnlyMetricsKeyMap[currentContext][metricKey]
  ) {
    return invertSecondaryOnlyMetricsKeyMap[currentContext][metricKey];
  } else {
    return defaultValue;
  }
};

export const isSecondaryOnlyMetric = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
}: {
  metricKey: string;
  context?: string;
}) => {
  const currentContext = getInvertContextMap(context);
  const { invertSecondaryOnlyMetricsKeyMap } = metricsStore.getState();
  return invertSecondaryOnlyMetricsKeyMap[currentContext] &&
    invertSecondaryOnlyMetricsKeyMap[currentContext][metricKey]
    ? true
    : false;
};

export const getSecondaryOnlyMetricByKey = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = null,
}: {
  metricKey: string;
  context?: string;
  defaultValue?: null | string;
}) => {
  const currentContext = getInvertContextMap(context);
  const { secondaryOnlyMetricsKeyMap } = metricsStore.getState();
  if (
    secondaryOnlyMetricsKeyMap[currentContext] &&
    secondaryOnlyMetricsKeyMap[currentContext][metricKey]
  ) {
    return secondaryOnlyMetricsKeyMap[currentContext][metricKey];
  } else {
    return defaultValue;
  }
};

export const getMetricUnitText = (unit) => {
  if (unit === BPS) {
    return BPS;
  }
  return ` ${unit}`;
};

const isMetricIncluded = (sourceList, metricKey) => {
  const sourceSet = new Set(sourceList);
  const mappedMetric =
    getPercentageMetricByKey({ metricKey }) || getInvertedPercentageMetricByKey({ metricKey });
  return sourceSet.has(metricKey) || sourceSet.has(mappedMetric);
};

// check if a metric is percentage
const isPercentageMetric = (metricKey) => getMetricUnitByKey({ metricKey }) === '%';

// combine with de-duplication
const combineWithMetricDeDuplication = (sourceList, items) =>
  Array.from(new Set([...sourceList, ...items]));

// exclude with de-duplication
const excludeWithMetricDeDuplication = (sourceList, items) => {
  const sourceSet = new Set(sourceList);
  items.forEach((i) => sourceSet.delete(i));
  return Array.from(sourceSet);
};

// generate a new metric list based on:
// source: source array
// changeObj: include and exclude arrays in an object
const genMetricList = (source, changeObj) => {
  // include
  const metrics = combineWithMetricDeDuplication(source, changeObj.include);
  // exclude
  return excludeWithMetricDeDuplication(metrics, changeObj.exclude);
};

// get metric exclude list for current preset
const getMetricExcludeList = (presetFid) => {
  if (dataFilterMetricsList[presetFid] !== undefined) {
    return dataFilterMetricsList[presetFid].exclude;
  }
  return [];
};

const getMetricByUnit = (unit: string, metricKey: string): string => {
  if (isRateMetric(metricKey)) {
    if (unit === '%') return metricKey;
    const numericMetric = getNumericMetric(metricKey);
    return unit === '%T'
      ? getPercentageLocalKeyFromPrimaryKey({ metricKey: numericMetric, defaultValue: numericMetric })
      : numericMetric;
  }
  if (unit === '%') {
    return getPercentageMetricByKey({
      metricKey,
      defaultValue: metricKey,
    });
  }
  if (unit === '%T') {
    return getPercentageLocalKeyFromPrimaryKey({ metricKey, defaultValue: metricKey });
  }
  return metricKey;
};

export const getPercentageLocalKeyFromPrimaryKey = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = null,
}: {
  metricKey: string;
  context?: string;
  defaultValue?: null | string;
}) => {
  const currentContext = getInvertContextMap(context);
  const { primaryToPercentageLocalKeyMap } = metricsStore.getState();
  if (
    primaryToPercentageLocalKeyMap[currentContext] &&
    primaryToPercentageLocalKeyMap[currentContext][metricKey]
  ) {
    return primaryToPercentageLocalKeyMap[currentContext][metricKey];
  } else {
    return defaultValue;
  }
};

export const getPrimaryKeyFromPercentageLocalKey = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = null,
}: {
  metricKey: string;
  context?: string;
  defaultValue?: null | string;
}) => {
  const currentContext = getInvertContextMap(context);
  const { percentageLocalToPrimaryKeyMap } = metricsStore.getState();
  if (
    percentageLocalToPrimaryKeyMap[currentContext] &&
    percentageLocalToPrimaryKeyMap[currentContext][metricKey]
  ) {
    return percentageLocalToPrimaryKeyMap[currentContext][metricKey];
  } else {
    return defaultValue;
  }
};

export const getUserDefinedDistributionMetric = (context: string = CURRENT_PRODUCT_MODE) => {
  const currentContext = getInvertContextMap(context);
  const { userDefinedMetricsMeta } = metricsStore.getState();

  if (userDefinedMetricsMeta[currentContext]) {
    return Object.entries(userDefinedMetricsMeta[currentContext])
      .filter(([, metric]: any) => metric.distribution?.length)
      .reduce((acc, [metricKey, metric]: any) => {
        acc[metricKey] = metric.distribution;
        return acc;
      }, {});
  } else {
    return {};
  }
};

export const getPreDefinedDistributionMetrics = (context: string = CURRENT_PRODUCT_MODE) => {
  const currentContext = getInvertContextMap(context);
  const { preDefinedMetricsMeta } = metricsStore.getState();

  if (preDefinedMetricsMeta[currentContext]) {
    return Object.entries(preDefinedMetricsMeta[currentContext])
      .filter(([, metric]: any) => metric.distribution?.length)
      .reduce((acc, [metricKey, metric]: any) => {
        acc[metricKey] = metric.distribution;
        return acc;
      }, {});
  } else {
    return {};
  }
};

export const getPercentileMetrics = () => {
  const { percentileMetrics } = metricsStore.getState();
  const currentContext = getInvertContextMap(CURRENT_PRODUCT_MODE);
  return percentileMetrics[currentContext] || [];
};

export {
  isMetricIncluded,
  getMetricByUnit,
  isPercentageMetric,
  getMetricExcludeList,
  combineWithMetricDeDuplication,
  excludeWithMetricDeDuplication,
  genMetricList,
};
